import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { Shield } from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { Footer } from '@/components/layout/Footer'
import { cn } from '@/lib/utils'

const SECTIONS = ['intro', 'cookies', 'privacy', 'rights'] as const

export function Privacy() {
  const { t, i18n } = useTranslation('privacy')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'
  const canonicalPath = `/${lang}/privacy`

  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: { duration: 0.6, ease: 'easeOut' as const },
      }

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
        noindex
      />

      <main id="main-content">
        {/* Hero */}
        <section className="bg-maia px-4 py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <Shield
              className="mx-auto mb-5 h-12 w-12 text-white"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <h1 className="font-display text-[clamp(2.5rem,6vw,4rem)] font-extrabold leading-[1.05] tracking-tight text-white">
              {t('hero.title')}
            </h1>
            <p className="mt-4 text-lg text-white/90">{t('hero.subtitle')}</p>
          </div>
        </section>

        {/* Content */}
        <section className="bg-cream px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-14">
              {SECTIONS.map((key) => (
                <motion.article
                  key={key}
                  {...fadeUp}
                  className="scroll-mt-24"
                  id={key}
                >
                  <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                    {t(`sections.${key}.title`)}
                  </h2>
                  <p className="mt-4 whitespace-pre-line leading-relaxed text-stone">
                    {t(`sections.${key}.text`)}
                  </p>
                </motion.article>
              ))}
            </div>

            {/* Data controller card */}
            <motion.aside
              {...fadeUp}
              className={cn(
                'mt-16 rounded-3xl border border-cord bg-white p-6 sm:p-8',
                'shadow-sm'
              )}
            >
              <h2 className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">
                {t('owner.title')}
              </h2>
              <dl className="mt-6 grid gap-4 text-stone sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.nameLabel')}
                  </dt>
                  <dd className="mt-1 font-medium text-ink">{t('owner.name')}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.phoneLabel')}
                  </dt>
                  <dd className="mt-1 font-medium text-ink">{t('owner.phone')}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.addressLabel')}
                  </dt>
                  <dd className="mt-1 font-medium text-ink">{t('owner.address')}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.vatLabel')}
                  </dt>
                  <dd className="mt-1 font-medium text-ink">{t('owner.vat')}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.reaLabel')}
                  </dt>
                  <dd className="mt-1 font-medium text-ink">{t('owner.rea')}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                    {t('owner.emailLabel')}
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={`mailto:${t('owner.email')}`}
                      className="font-medium text-maia underline-offset-4 hover:underline"
                    >
                      {t('owner.email')}
                    </a>
                  </dd>
                </div>
              </dl>
            </motion.aside>

            <p className="mt-10 text-center text-sm text-muted">
              {t('lastUpdated')}
            </p>
          </div>
        </section>
      </main>

      <Footer variant="maia" />
    </>
  )
}
